/**
 * Floodlight — the type and polish layer on top of the original BetOptimized
 * look (v1.71, Trev Oct 19: "keep our current background and glassmorphic
 * style"; "our purple and blue accents remain"). The ground is the original
 * black with its blue glow, surfaces are glass (white washes, hairline
 * borders, backdrop blur, rounded corners) and the accents are the original
 * purple and blue. Floodlight itself contributes Archivo type, kickers,
 * tabular numerals and motion. Dark only (the v1.69 light mode is gone).
 *
 * Token names are kept from v1.69 so every screen re-skins from here:
 *   paper            the page ground (black; FLPage paints the glow)
 *   card / paper2    glass surfaces (raised / sunken, e.g. inputs)
 *   stage            a stronger glass panel (was the "ink panel")
 *   ink, ink2, muted text steps
 *   rule, hair, hair2 borders and dividers
 *   green            the primary ACCENT FILL (now purple); text on it = onGreen
 *   greenInk         accent text on the ground (light purple)
 *   greenOnInk       accent text on a stage
 *   blue / blueInk   the secondary accent
 *   pos              positive EV / best price (stays green: it means "good")
 *   paper, paperSoft, paperFaint on a stage: use onStage / stageSoft / stageFaint
 *
 * Every token is a CSS custom property (`var(--fl-paper)`), which
 * react-native-web passes straight to the DOM.
 *
 * The site chrome (navigation header, game and injury tickers) never reads these tokens.
 */
import { Platform } from 'react-native';

const GLASS = {
    paper: '#000000',
    paper2: 'rgba(255,255,255,0.05)',
    card: 'rgba(255,255,255,0.04)',
    stage: 'rgba(255,255,255,0.07)',
    ink: 'rgba(255,255,255,0.92)',
    ink2: 'rgba(255,255,255,0.72)',
    muted: 'rgba(255,255,255,0.56)',
    rule: 'rgba(255,255,255,0.14)',
    hair: 'rgba(255,255,255,0.10)',
    hair2: 'rgba(255,255,255,0.20)',
    green: '#7C3AED',      // accent fill (purple); white text on it is 5.7:1
    greenInk: '#C4A1FF',   // accent text on the ground
    greenOnInk: '#C4A1FF', // accent text on a stage
    greenWash: 'rgba(162,89,255,0.16)',
    blue: '#2563EB',
    blueInk: '#93B4FF',
    blueWash: 'rgba(59,130,246,0.16)',
    pos: '#4ADE80',
    posWash: 'rgba(74,222,128,0.14)',
    red: '#FF6A5C',
    redWash: 'rgba(255,106,92,0.14)',
    amber: '#F0B43C',
    amberWash: 'rgba(240,180,60,0.16)',
    onStage: 'rgba(255,255,255,0.92)',
    stageSoft: 'rgba(255,255,255,0.72)',
    stageFaint: 'rgba(255,255,255,0.56)',
    paperSoft: 'rgba(255,255,255,0.72)',
    paperFaint: 'rgba(255,255,255,0.56)',
    inkSoft: 'rgba(255,255,255,0.65)',
    inkFaint: 'rgba(255,255,255,0.25)',
    inkWash: 'rgba(255,255,255,0.06)',
    grid: 'rgba(255,255,255,0)',
    inkRgb: '255,255,255',
    paperRgb: '0,0,0',
};

/** One palette now; kept as a map so callers that index by theme keep working. */
export const FL_PALETTES = { dark: GLASS };

export const FL_THEMES = ['dark'];
export const FL_DEFAULT_THEME = 'dark';

/** Glass surface: spread into a View style (web blur; native gets the wash). */
export const glass = ({ strong = false, radius = 20 } = {}) => ({
    backgroundColor: strong ? 'var(--fl-stage)' : 'var(--fl-card)',
    borderWidth: 1,
    borderColor: 'var(--fl-hair)',
    borderRadius: radius,
    ...(Platform.OS === 'web' ? { backdropFilter: 'blur(18px) saturate(140%)', WebkitBackdropFilter: 'blur(18px) saturate(140%)' } : {}),
});

/** The accent gradient (purple → blue) for primary fills on web. */
export const ACCENT_GRADIENT = 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 55%, #2563EB 100%)';

const cssName = (key) => `--fl-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

/** `{ paper: 'var(--fl-paper)', … }` plus onGreen, which never changes. */
export const FL = {
    ...Object.fromEntries(Object.keys(FL_PALETTES.dark).map((k) => [k, `var(${cssName(k)})`])),
    onGreen: '#FFFFFF',   // text and icons on the accent fill (5.7:1 on #7C3AED)
};

/** The palette block on :root. web/index.html inlines the same text. */
export function flThemeCss() {
    const block = (p) => Object.entries(p).map(([k, v]) => `${cssName(k)}:${v};`).join('');
    return `:root{${block(FL_PALETTES.dark)}}`;
}

const WEB = Platform.OS === 'web';

/** Archivo is variable: wdth 62–125, wght 100–900. Native falls back to the system face. */
export const FONT = WEB ? 'Archivo, "Helvetica Neue", Arial, sans-serif' : undefined;

/** Display: heavy, expanded, uppercase, tight. Spread into a Text style. */
export const display = (size, { width = 118, weight = '800', lineHeight = 0.9 } = {}) => ({
    fontFamily: FONT,
    fontSize: size,
    lineHeight: Math.round(size * lineHeight),
    fontWeight: weight,
    letterSpacing: -size * 0.035,
    textTransform: 'uppercase',
    color: FL.ink,
    ...(WEB ? { fontStretch: `${width}%` } : {}),
});

/** Small uppercase label (kickers, column heads, meta). */
export const label = (size = 11) => ({
    fontFamily: FONT,
    fontSize: size,
    fontWeight: '600',
    letterSpacing: size * 0.09,
    textTransform: 'uppercase',
    color: FL.muted,
});

/** Body copy. */
export const body = (size = 16) => ({
    fontFamily: FONT,
    fontSize: size,
    lineHeight: Math.round(size * 1.5),
    color: FL.ink2,
});

/** Tabular numerals for prices, odds, counts. */
export const nums = WEB ? { fontVariant: ['tabular-nums'] } : {};

/** Page gutter by viewport width. */
export const gutter = (w) => (w >= 1100 ? 48 : w >= 700 ? 28 : 16);
